import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type FTemplateSectionFragment,
  type FWaypointTemplateFragment,
  useUpdateWaypointTemplateSectionMutation,
} from '@dashboard/generated/wp/platform';
import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Collapse } from '@mui/material';

import { alertSuccess } from '@waypoint/react/notifications';

import { SectionDragOverlay, TemplateDragOverlay } from './DragOverlays';
import TemplateSection from './TemplateSection';
import { useMutateableSections } from './useMutateableSections';

type TEventData = {
  type: 'section' | 'global' | 'template';
  id: string;
};
type SectionedTemplates = {
  globalTemplates: FWaypointTemplateFragment[];
  templatesBySection: Record<string, FWaypointTemplateFragment[]>;
};

type MinTemplate = Pick<FWaypointTemplateFragment, 'id' | 'sectionId'>;

function useMutateableTemplates(templates: FWaypointTemplateFragment[]) {
  const [updateWaypointTemplateSection] = useUpdateWaypointTemplateSectionMutation();

  const [minTemplates, setMinTemplates] = useState<MinTemplate[]>(() => {
    return templates.map((t) => ({ id: t.id, sectionId: t.sectionId }));
  });

  useEffect(() => {
    setMinTemplates(templates.map((t) => ({ id: t.id, sectionId: t.sectionId })));
  }, [templates]);

  const { globalTemplates, templatesBySection } = useMemo(() => {
    const templatesById = new Map(templates.map((t) => [t.id, t]));
    const result: SectionedTemplates = { globalTemplates: [], templatesBySection: {} };
    for (const t of minTemplates) {
      const template = templatesById.get(t.id);
      if (!template) {
        continue;
      }
      const sectionId = t.sectionId;
      if (!sectionId) {
        result.globalTemplates.push(template);
        continue;
      }
      const section = result.templatesBySection[sectionId] ?? [];
      section.push(template);
      result.templatesBySection[sectionId] = section;
    }
    return result;
  }, [minTemplates, templates]);

  const setTemplateSection = useCallback(
    async (templateId: string, templateSectionId: string | null) => {
      setMinTemplates((prev) => {
        const result = [...prev];
        const index = result.findIndex((t) => t.id === templateId);
        result[index].sectionId = templateSectionId;
        return result;
      });
      await updateWaypointTemplateSection({ variables: { templateId, templateSectionId } });
    },
    [updateWaypointTemplateSection]
  );

  return {
    globalTemplates,
    templatesBySection,
    setTemplateSection,
  };
}

type PageContentProps = {
  sections: FTemplateSectionFragment[];
  templates: FWaypointTemplateFragment[];
};
export default function PageContent({ sections: originalSections, templates }: PageContentProps) {
  const [sections, reorderSections] = useMutateableSections(originalSections);
  const { globalTemplates, templatesBySection, setTemplateSection } = useMutateableTemplates(templates);

  const [activeData, setActiveData] = useState<TEventData | null>(null);
  const [recentDropSection, setRecentDropSection] = useState<TEventData | null>(null);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 8 } }));

  const sectionRef = useRef<HTMLDivElement>(null);
  const [sectionsWidth, setSectionsWidth] = useState<number | null>(null);

  const handleDragStart = (ev: DragStartEvent) => {
    setActiveData(ev.active.data.current as unknown as TEventData);

    const el = sectionRef.current;
    if (el) {
      setSectionsWidth(el.getBoundingClientRect().width);
    }
  };

  const handleDragEnd = async (ev: DragEndEvent) => {
    setActiveData(null);

    const { active, over } = ev;
    if (!over) {
      return;
    }

    const activeData = active.data.current as unknown as TEventData;
    const overData = over.data.current as unknown as TEventData;
    if (activeData.type === 'section' && overData.type === 'section') {
      await reorderSections(activeData.id, overData.id);
      alertSuccess('Sections reordered');
    }

    if (activeData.type === 'template' && (overData.type === 'section' || overData.type === 'global')) {
      const templateSectionId = overData.type === 'section' ? overData.id : null;
      await setTemplateSection(activeData.id, templateSectionId);
      alertSuccess('Template moved');
      setRecentDropSection(overData);
      setTimeout(() => {
        setRecentDropSection((prev) => (prev === overData ? null : prev));
      }, 800);
    }
  };

  const handleDragCancel = () => {
    setActiveData(null);
  };

  let activeSection = null;
  if (activeData?.type === 'section') {
    activeSection = originalSections.find((s) => s.id === activeData.id) ?? null;
  }
  let activeTemplate = null;
  if (activeData?.type === 'template') {
    activeTemplate = templates.find((t) => t.id === activeData.id) ?? null;
  }

  useEffect(() => {
    if (!activeData) {
      return undefined;
    }
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'grabbing';
    return () => {
      document.body.style.cursor = previous;
    };
  }, [activeData]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sections} strategy={verticalListSortingStrategy}>
        {sections.map((s) => (
          <TemplateSection
            key={s.id}
            section={s}
            templates={templatesBySection[s.id] ?? []}
            isRecentDrop={recentDropSection?.type === 'section' && recentDropSection.id === s.id}
          />
        ))}
      </SortableContext>
      <Collapse in={globalTemplates.length > 0 || activeTemplate !== null} unmountOnExit>
        <div ref={sectionRef}>
          <TemplateSection
            section={null}
            templates={globalTemplates}
            isRecentDrop={recentDropSection?.type === 'global'}
          />
        </div>
      </Collapse>
      <DragOverlay modifiers={activeSection !== null ? [({ transform }) => ({ ...transform, x: 0 })] : []}>
        <TemplateDragOverlay template={activeTemplate} />
        <SectionDragOverlay sectionsWidth={sectionsWidth} section={activeSection} />
      </DragOverlay>
    </DndContext>
  );
}
